/**
 * Unified reducer for managing track-related state across Manager and Crew applications.
 * Consolidates state handling for tracks, shipments, selections, and loading states.
 */

export const TRACK_ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_TRACKS: "SET_TRACKS",
  SET_SELECTED_TRACK: "SET_SELECTED_TRACK",
  SET_TARGET_TRACK: "SET_TARGET_TRACK",
  SET_PROCESSED_CARS_MAP: "SET_PROCESSED_CARS_MAP",
  SET_STALE_TRACKS: "SET_STALE_TRACKS",
};

export const trackReducer = (state, action) => {
  switch (action.type) {
    case TRACK_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case TRACK_ACTIONS.SET_TRACKS: {
      const nextTracks = typeof action.payload === "function"
        ? action.payload(state.tracks)
        : action.payload;
      
      // CRITICAL: Keep selectedTrack and targetTrack in sync with updated tracks array
      // to ensure UI components (like capacity bars) see the latest data.
      let nextSelected = state.selectedTrack;
      let nextTarget = state.targetTrack;

      if (nextSelected?.id) {
        const found = (nextTracks || []).find(t => t.id === nextSelected.id);
        if (found) nextSelected = found;
      }
      if (nextTarget?.id) {
        const found = (nextTracks || []).find(t => t.id === nextTarget.id);
        if (found) nextTarget = found;
      }

      return {
        ...state,
        tracks: nextTracks,
        selectedTrack: nextSelected,
        targetTrack: nextTarget,
      };
    }

    case TRACK_ACTIONS.SET_SELECTED_TRACK:
      return {
        ...state,
        selectedTrack: action.payload,
      };

    case TRACK_ACTIONS.SET_TARGET_TRACK:
      return {
        ...state,
        targetTrack: action.payload,
      };

    case TRACK_ACTIONS.SET_PROCESSED_CARS_MAP:
      return {
        ...state,
        processedCarsMap: typeof action.payload === "function"
          ? action.payload(state.processedCarsMap)
          : action.payload,
      };

    case TRACK_ACTIONS.SET_STALE_TRACKS:
      return {
        ...state,
        staleTracks: action.payload,
      };

    default:
      return state;
  }
};

/**
 * Factory for creating track actions bound to a dispatch function.
 * 
 * @param {Function} dispatch - React useReducer dispatch function
 * @returns {Object} Bound action creators
 */
export const createTrackActions = (dispatch) => {
  return {
    setLoading: (loading) =>
      dispatch({ type: TRACK_ACTIONS.SET_LOADING, payload: loading }),
    setTracks: (tracks) =>
      dispatch({ type: TRACK_ACTIONS.SET_TRACKS, payload: tracks }),
    setSelectedTrack: (track) =>
      dispatch({ type: TRACK_ACTIONS.SET_SELECTED_TRACK, payload: track }),
    setTargetTrack: (track) =>
      dispatch({ type: TRACK_ACTIONS.SET_TARGET_TRACK, payload: track }),
    setProcessedCarsMap: (map) =>
      dispatch({ type: TRACK_ACTIONS.SET_PROCESSED_CARS_MAP, payload: map }),
    setStaleTracks: (staleTracks) =>
      dispatch({ type: TRACK_ACTIONS.SET_STALE_TRACKS, payload: staleTracks }),
  };
};
